import { IgSelect, IgTypography } from "@intelligems/ig-theme";
import type {
  OnsiteUpsellDiscount,
  UpsellCompareAtPriceDisplay,
  UpsellDiscountType,
} from "@intelligems/ig-types";
import {
  UpsellCompareAtPriceDisplayList,
  UpsellDiscountTypeList,
} from "@intelligems/ig-types";
import { InputAdornment, MenuItem } from "@mui/material";
import Stack from "@mui/material/Stack";
import { Panel } from "@src/features/experienceBuilder/modifications/common/panel/Panel";
import { useCurrency } from "@src/utils/useCurrency";

const DISCOUNT_TYPE_LABELS: Record<UpsellDiscountType, string> = {
  none: "No discount",
  percentage: "Percentage off",
  fixed: "Fixed amount off",
};

const COMPARE_AT_LABELS: Record<UpsellCompareAtPriceDisplay, string> = {
  none: "Don't show a comparison price",
  compareAtPrice: "Show compare-at price",
  regularPrice: "Show regular price",
};

const EMPTY: OnsiteUpsellDiscount = {
  discountType: "none",
  discountValue: null,
  compareAtPriceDisplay: "none",
};

export const DiscountSection = ({
  discount,
  valueError,
  onChange,
}: {
  discount: OnsiteUpsellDiscount | null;
  valueError?: boolean;
  onChange: (next: OnsiteUpsellDiscount | null) => void;
}) => {
  const { symbol } = useCurrency();
  const current = discount ?? EMPTY;
  const hasDiscount = current.discountType !== "none";

  return (
    <Stack spacing={2}>
      <Stack spacing={1}>
        <Panel.Header>Discount</Panel.Header>
        <IgTypography variant="body2" color="textSecondary">
          Save the experience to preview the discount in the cart. These
          discounts will stack by default. To prevent this discount from
          combining with other Shopify or Intelligems discounts, make sure those
          other discounts are configured to block stacking.
        </IgTypography>
      </Stack>
      <IgSelect<UpsellDiscountType>
        fullWidth
        size="small"
        value={current.discountType}
        onChange={(e) => {
          const discountType = e.target.value;
          // "none" means no discount at all — omit the whole object.
          if (discountType === "none") {
            onChange(null);
          } else {
            onChange({ ...current, discountType });
          }
        }}
      >
        {UpsellDiscountTypeList.map((t) => (
          <MenuItem key={t} value={t}>
            {DISCOUNT_TYPE_LABELS[t]}
          </MenuItem>
        ))}
      </IgSelect>

      {hasDiscount && (
        <>
          <Panel.TextField
            label="Amount"
            type="number"
            value={
              current.discountValue == null ? "" : String(current.discountValue)
            }
            onChange={(e) =>
              onChange({
                ...current,
                discountValue:
                  e.target.value === "" ? null : Number(e.target.value),
              })
            }
            error={valueError}
            slotProps={{
              input:
                current.discountType === "fixed"
                  ? {
                      startAdornment: (
                        <InputAdornment position="start">
                          {symbol}
                        </InputAdornment>
                      ),
                    }
                  : {
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    },
            }}
          />

          <IgSelect<UpsellCompareAtPriceDisplay>
            label="Comparison price"
            fullWidth
            size="small"
            value={current.compareAtPriceDisplay}
            onChange={(e) =>
              onChange({ ...current, compareAtPriceDisplay: e.target.value })
            }
          >
            {UpsellCompareAtPriceDisplayList.map((d) => (
              <MenuItem key={d} value={d}>
                {COMPARE_AT_LABELS[d]}
              </MenuItem>
            ))}
          </IgSelect>
        </>
      )}
    </Stack>
  );
};
